import _ from 'lodash'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import {
	Props,
	IPropsStore,
	PageFeatureConfigSymbol,
	SiteFeatureConfigSymbol,
	EditorFeatureConfigSymbol,
	PageScrollRegistrarSymbol,
	CarmiInstance,
	ViewerModelSym,
	ViewerModel,
} from '@wix/thunderbolt-symbols'
import { getFullId, loadScriptWithRequireJS } from '@wix/thunderbolt-commons'
import { ISiteScrollBlocker, SiteScrollBlockerSymbol } from 'feature-site-scroll-blocker'
import { IWindowScrollAPI, WindowScrollApiSymbol } from 'feature-window-scroll'
import { IOoiTpaSharedConfig, OoiTpaSharedConfigSymbol } from 'feature-ooi-tpa-shared-config'
import type { IPageScrollRegistrar } from 'feature-page-scroll'
import { createHostProps } from '../hostProps'
import { name, ReactLoaderForOOISymbol } from '../symbols'
import type { OOIComponentLoader, OOIPageConfig, OOISiteConfig, OOIEditorConfig } from '../types'
import { IViewportWidthProvider, OOIViewportWidthProviderSymbol } from '../viewportWidthProvider'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import type { OOICssConfig } from '@wix/thunderbolt-becky-root'

export const ooiComponentWillMount = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		named(PageFeatureConfigSymbol, name),
		ReactLoaderForOOISymbol,
		Props,
		named(SiteFeatureConfigSymbol, name),
		SiteScrollBlockerSymbol,
		WindowScrollApiSymbol,
		PageScrollRegistrarSymbol,
		OoiTpaSharedConfigSymbol,
		OOIViewportWidthProviderSymbol,
		CarmiInstance,
		ViewerModelSym,
	],
	(
		ooiEditorConfig: OOIEditorConfig,
		ooiPageConfig: OOIPageConfig,
		ooiComponentsLoader: OOIComponentLoader,
		propsStore: IPropsStore,
		{ viewMode, formFactor }: OOISiteConfig,
		siteScrollBlocker: ISiteScrollBlocker,
		windowScrollApi: IWindowScrollAPI,
		{ registerToThrottledScroll }: IPageScrollRegistrar,
		{ getFontsConfig }: IOoiTpaSharedConfig,
		{ getViewportWidth }: IViewportWidthProvider,
		{ ooi: setters }: DSCarmi,
		{ mode: { debug } }: ViewerModel
	) => {
		const loadCssConfigIfNeeded = async (widgetId: string) => {
			if (ooiEditorConfig.ooiCssConfigUrls[widgetId]) {
				return loadScriptWithRequireJS<{ cssConfig: OOICssConfig }>(ooiEditorConfig.ooiCssConfigUrls[widgetId])
					.then((module) => {
						setters.setOoiCssConfigStore(widgetId, module.cssConfig)
					})
					.catch((error) => {
						if (debug) {
							console.error(`Failed to fetch OOI css config for widget id: ${widgetId}`, error)
						}
					})
			}
		}

		return {
			componentTypes: ['tpaWidgetNative'],
			async componentWillMount({ id: compId }: { id: string }) {
				const { ooiComponents, accessibilityEnabled } = ooiPageConfig
				const compData = ooiComponents[compId] || ooiComponents[getFullId(compId)]
				const { widgetId } = compData
				const [{ component: ReactComponent }] = await Promise.all([
					ooiComponentsLoader.getComponent(compData.widgetId),
					loadCssConfigIfNeeded(widgetId), // sideEffect - loads css config which will also trigger the widget's css calculation using it
				])

				const hostProps = createHostProps({
					compData,
					pageId: 'c1dmp', // TODO: implement pageId
					accessibilityEnabled,
					formFactor,
					viewMode,
					siteScrollBlocker,
					windowScrollApi,
					registerToThrottledScroll,
					fonts: getFontsConfig(),
					getViewportWidth,
				})
				propsStore.update({
					[compId]: {
						ReactComponent,
						host: {
							...hostProps,
							registerToComponentDidLayout: _.noop,
							unregisterFromComponentDidLayout: _.noop,
						},
					},
				})
			},
		}
	}
)
